import moment from 'moment';
import formatAsCurrency from './format-as-currency';

/**
 * Determines whether a NUMERIC field's current value falls outside its resolved bounds.
 * Advisory only (BR-F2) — never affects `voucher_valid`.
 * @param {*} value - the field's current live value
 * @param {{category: string, min: number, max: number}|undefined} bounds
 * @returns {boolean}
 */
const isNumericOutOfRange = (value, bounds) => {
  if (!bounds || bounds.category !== 'NUMERIC') return false;
  if (value === null || value === undefined || value === '') return false;
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return false;
  return numericValue < bounds.min || numericValue > bounds.max;
};

/**
 * Builds the "Typical range: ..." warning message for an out-of-range field (e.g.
 * "Typical range: $2-$168/sqft per year."), matching the approved `Review.dc.html`
 * reference design exactly -- no leading "Value is outside..." sentence, an en dash
 * between the min/max, whole-dollar currency (no cents), and a trailing period.
 * Returns '' if bounds are missing/incomplete (nothing meaningful to show).
 * @param {{min: number, max: number}|{minDate: string|null, maxDate: string|null}|undefined} bounds
 * @param {'currency'|'percent'|'number'|'date'|undefined} [format] - defaults to plain number formatting
 * @param {string} [unit] - optional suffix appended after the range (e.g. "months", "/SF")
 * @returns {string}
 */
const formatOutOfRangeMessage = (bounds, format, unit) => {
  if (format === 'date') {
    // Every DATE rule currently seeded on the backend either has a fixed `maxDate` (from
    // `max_offset_days`) and/or a `minDateReference` (a cross-field comparison, e.g.
    // "occupancy_date must not be before start_date" -- the backend only sends the
    // `attributeKey`/`operator` metadata since it has no per-lease context to resolve an
    // actual date, so the caller (e.g. PropertyReview.js) resolves the referenced field's
    // live `value`/display `label` and merges them into `bounds.minDateReference` before
    // calling here). `minDate` itself is unused by any seeded DATE row today but is still
    // handled for completeness. Unlike NUMERIC bounds, a DATE bound routinely has only ONE
    // side present -- handle min-only/max-only/reference/both instead of requiring both
    // like the NUMERIC branch below.
    if (!bounds) return '';
    const { minDate, maxDate, minDateReference: ref } = bounds;
    const hasResolvedRef = !!(ref && ref.value && ref.label);
    if (!minDate && !maxDate && !hasResolvedRef) return '';
    const formatDate = value => moment(value).format('M/D/YY');

    if (minDate && maxDate) {
      return `Typical range: ${formatDate(minDate)}\u2013${formatDate(maxDate)}.`;
    }

    if (hasResolvedRef) {
      const refText = `${ref.label} (${formatDate(ref.value)})`;
      const minPhraseByOperator = {
        GT: `after ${refText}`,
        GTE: `on or after ${refText}`,
        LT: `before ${refText}`,
        LTE: `on or before ${refText}`,
      };
      const minPhrase = minPhraseByOperator[ref.operator];
      if (minPhrase && maxDate) return `Typical range: ${minPhrase} and on or before ${formatDate(maxDate)}.`;
      if (minPhrase) return `Typical range: ${minPhrase}.`;
    }

    if (maxDate) return `Typical range: on or before ${formatDate(maxDate)}.`;
    return `Typical range: on or after ${formatDate(minDate)}.`;
  }

  if (!bounds || bounds.min === null || bounds.min === undefined || bounds.max === null || bounds.max === undefined) {
    return '';
  }
  const formatValue = (value) => {
    if (format === 'currency') return formatAsCurrency(value, 0);
    if (format === 'percent') return `${value}%`;
    // Plain 'number' bounds (e.g. Lease Term months) show without commas -- "1200", not
    // "1,200" -- and `Number(...)` drops trailing decimal zeros from backend Decimal
    // strings like "1.00" first.
    return Number(value).toString();
  };
  // A "/"-prefixed unit (e.g. "/SF", "/sqft per year") is appended directly against the
  // max value with no space, matching the reference design's "$0-$500/SF." -- a plain-word
  // unit (e.g. "months") keeps its leading space: "0-120 months.".
  let unitSuffix = '';
  if (unit) unitSuffix = unit.startsWith('/') ? unit : ` ${unit}`;
  return `Typical range: ${formatValue(bounds.min)}\u2013${formatValue(bounds.max)}${unitSuffix}.`;
};

/**
 * Determines whether a DATE field's current value falls outside its resolved bounds.
 * Advisory only (BR-F2) — never affects `voucher_valid`.
 * @param {*} value - the field's current live value
 * @param {{category: string, minDate: string|null, maxDate: string|null}|undefined} bounds
 * @returns {boolean}
 */
const isDateOutOfRange = (value, bounds) => {
  if (!bounds || bounds.category !== 'DATE') return false;
  if (!value) return false;
  const dateValue = moment(value);
  if (!dateValue.isValid()) return false;
  if (bounds.minDate && dateValue.isBefore(moment(bounds.minDate), 'day')) return true;
  if (bounds.maxDate && dateValue.isAfter(moment(bounds.maxDate), 'day')) return true;

  // Cross-field min bound (e.g. "occupancy_date must not be before start_date") -- see
  // `minDateReference` handling in `formatOutOfRangeMessage` above for how `ref.value`/
  // `ref.label` get resolved by the caller. No live value yet (referenced field still
  // blank) means nothing to compare against, so skip rather than false-flagging.
  const ref = bounds.minDateReference;
  if (ref && ref.value) {
    const refDate = moment(ref.value);
    if (refDate.isValid()) {
      if (ref.operator === 'GT' && !dateValue.isAfter(refDate, 'day')) return true;
      if (ref.operator === 'GTE' && dateValue.isBefore(refDate, 'day')) return true;
      if (ref.operator === 'LT' && !dateValue.isBefore(refDate, 'day')) return true;
      if (ref.operator === 'LTE' && dateValue.isAfter(refDate, 'day')) return true;
    }
  }
  return false;
};

/**
 * Single gate for the whole broker-verification experience (editable Review panel, Voucher
 * Verification step, metrics). Early invoices keep the original read-only Review, so the
 * feature flag alone is not enough.
 */
const isBrokerVerificationActive = (featureFlags, isEarlyInvoice) => (
  !!featureFlags?.broker_verification_flag && !isEarlyInvoice
);

export {
  isBrokerVerificationActive,
  isNumericOutOfRange,
  isDateOutOfRange,
  formatOutOfRangeMessage,
};

